import React, { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  CheckCircle2, ChevronDown, ChevronRight, KeyRound, ListChecks, Loader2,
  Plus, RefreshCw, ShieldAlert, Trash2, TrendingUp,
} from "lucide-react";

const num = (v) => (v == null || v === "" ? "—" : Number(v).toLocaleString("en-AU"));
const moneyish = (v) => (v == null || v === "" ? "—" : `$${Number(v).toLocaleString("en-AU")}`);

/* ── Master key card ────────────────────────────────────────────── */
function MasterKeyCard({ master, reload }) {
  const [key, setKey] = useState("");
  const [busy, setBusy] = useState("");

  const save = async () => {
    setBusy("save");
    try {
      const { data } = await api.put("/semrush/master/api-key", { api_key: key });
      toast.success(`Key verified — ${num(data.units_balance)} API units available`);
      setKey(""); reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Key rejected"); }
    finally { setBusy(""); }
  };
  const verify = async () => {
    setBusy("verify");
    try {
      const { data } = await api.post("/semrush/master/verify");
      toast.success(`Key OK — ${num(data.units_balance)} units remaining`);
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Verification failed"); }
    finally { setBusy(""); }
  };
  const remove = async () => {
    if (!window.confirm("Remove the SEMrush master API key?")) return;
    await api.delete("/semrush/master/api-key"); reload();
  };

  return (
    <Card data-testid="semrush-master-card"><CardContent className="p-5 space-y-3">
      <div className="flex items-center gap-2">
        <KeyRound className="h-4 w-4 text-[#002FA7]" />
        <span className="font-semibold text-sm">Master API key</span>
        {master?.has_key ? (
          <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">
            <CheckCircle2 className="h-3 w-3 mr-1" /> Connected
          </Badge>
        ) : (
          <Badge variant="outline" className="bg-amber-50 text-amber-800 border-amber-200">Not configured</Badge>
        )}
        {master?.units_balance != null && (
          <span className="text-xs text-zinc-500" data-testid="semrush-units-balance">
            {num(master.units_balance)} API units remaining
          </span>
        )}
      </div>
      <div className="flex gap-2 max-w-xl">
        <Input type="password" placeholder="SEMrush API key (Profile → Subscription info → API units)"
          value={key} data-testid="semrush-key-input"
          onChange={(e) => setKey(e.target.value)} />
        <Button onClick={save} disabled={!key || busy === "save"} data-testid="semrush-key-save-btn">
          {busy === "save" && <Loader2 className="h-4 w-4 animate-spin mr-1" />} Save &amp; verify
        </Button>
        {master?.has_key && (
          <>
            <Button variant="outline" onClick={verify} disabled={busy === "verify"}
              data-testid="semrush-key-verify-btn">
              {busy === "verify" ? <Loader2 className="h-4 w-4 animate-spin" /> : "Re-verify"}
            </Button>
            <Button variant="outline" onClick={remove}>
              <Trash2 className="h-4 w-4 text-red-500" />
            </Button>
          </>
        )}
      </div>
      {master?.last_verified_at && (
        <div className="text-xs text-zinc-400">
          Last verified {master.last_verified_at.slice(0, 16).replace("T", " ")} UTC
        </div>
      )}
    </CardContent></Card>
  );
}

/* ── Plan capabilities dialog ───────────────────────────────────── */
function CapabilitiesCheck({ defaultDomain }) {
  const [open, setOpen] = useState(false);
  const [domain, setDomain] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const run = async () => {
    setBusy(true); setResult(null);
    try {
      const { data } = await api.post("/semrush/capabilities",
        { target: domain || defaultDomain, database: "au" });
      setResult(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Probe failed"); }
    finally { setBusy(false); }
  };

  return (
    <>
      <Button variant="outline" size="sm" data-testid="semrush-capabilities-btn"
        onClick={() => { setDomain(defaultDomain || ""); setOpen(true); }}>
        <ListChecks className="h-4 w-4 mr-1" /> Check plan capabilities
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl" data-testid="semrush-capabilities-dialog">
          <DialogHeader>
            <DialogTitle>SEMrush plan capabilities</DialogTitle>
            <DialogDescription>
              Runs a 1-row probe of every report type against your key (uses a few API units).
            </DialogDescription>
          </DialogHeader>
          <div className="flex gap-2">
            <Input value={domain} placeholder="Probe domain e.g. gbegroup.com.au"
              onChange={(e) => setDomain(e.target.value)} />
            <Button onClick={run} disabled={busy || !(domain || defaultDomain)}
              data-testid="semrush-capabilities-run-btn">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Run probe"}
            </Button>
          </div>
          {result && (
            <div className="space-y-1 max-h-96 overflow-y-auto">
              {Object.entries(result.capabilities).map(([name, r]) => (
                <div key={name} className="flex items-start gap-2 text-sm border-b py-1.5">
                  <Badge variant="outline" className={r.available
                    ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                    : "bg-red-50 text-red-700 border-red-200"}>
                    {r.available ? "OK" : "N/A"}
                  </Badge>
                  <div className="flex-1">
                    <div className="font-medium">{name}</div>
                    {r.note && <div className="text-xs text-zinc-500">{r.note}</div>}
                  </div>
                </div>
              ))}
              <div className="text-xs text-zinc-400 pt-1">
                Units remaining: {num(result.units_balance)}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

function ToxicityCard({ tox, error }) {
  if (error) {
    return (
      <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded p-3 flex gap-2"
        data-testid="semrush-toxicity-warning">
        <ShieldAlert className="h-4 w-4 shrink-0 mt-0.5" />
        <span><b>Backlink toxicity:</b> {error}</span>
      </div>
    );
  }
  if (!tox) return null;
  const raw = tox.raw || {};
  const flat = {};
  const walk = (o, prefix = "") => Object.entries(o || {}).forEach(([k, v]) => {
    if (v && typeof v === "object" && !Array.isArray(v)) walk(v, `${prefix}${k}.`);
    else if (typeof v === "number" || typeof v === "string") flat[`${prefix}${k}`] = v;
  });
  walk(raw);
  const pick = (...names) => {
    const hit = Object.keys(flat).find((k) =>
      names.some((n) => k.toLowerCase().includes(n)));
    return hit != null ? flat[hit] : null;
  };
  const score = pick("toxic_score", "toxicity_score", "overall_toxic");
  const toxic = pick("toxic_num", "toxic_count") ?? pick("toxic");
  const potentially = pick("potentially");
  const nonToxic = pick("non_toxic", "nontoxic", "healthy");
  const total = pick("total", "all_num", "backlinks_num");
  const cards = [["Toxicity score", score], ["Toxic links", toxic],
    ["Potentially toxic", potentially], ["Non-toxic", nonToxic],
    ["Links analysed", total]].filter(([, v]) => v != null);
  return (
    <Card data-testid="semrush-toxicity-card"><CardContent className="p-4">
      <div className="font-semibold text-sm mb-2 flex items-center gap-2">
        <ShieldAlert className="h-4 w-4 text-[#002FA7]" /> Backlink toxicity (Backlink Audit)
      </div>
      {cards.length ? (
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-2">
          {cards.map(([l, v]) => (
            <div key={l} className="rounded border p-2">
              <div className="text-[11px] text-zinc-500">{l}</div>
              <div className="text-lg font-bold">{num(v)}</div>
            </div>
          ))}
        </div>
      ) : (
        <pre className="text-xs text-zinc-500 max-h-40 overflow-auto bg-zinc-50 rounded p-2">
          {JSON.stringify(raw, null, 1).slice(0, 1200)}
        </pre>
      )}
    </CardContent></Card>
  );
}

/* ── Snapshot data view ─────────────────────────────────────────── */
function SnapshotView({ data }) {
  const s = data?.sections || {};
  const ov = (s.overview || [])[0] || {};
  const bl = s.backlinks || {};
  return (
    <div className="space-y-4 pt-3" data-testid="semrush-snapshot">
      <div className="grid grid-cols-2 lg:grid-cols-6 gap-2">
        {[["SEMrush Rank", num(ov.rank)],
          ["Organic keywords", num(ov.organic_keywords)],
          ["Organic traffic /mo", num(ov.organic_traffic)],
          ["Traffic cost", moneyish(ov.organic_cost)],
          ["Authority score", num(bl.authority_score)],
          ["Backlinks", num(bl.backlinks_total)]].map(([l, v]) => (
          <Card key={l}><CardContent className="p-3">
            <div className="text-[11px] text-zinc-500">{l}</div>
            <div className="text-lg font-bold">{v}</div>
          </CardContent></Card>
        ))}
      </div>
      {Object.entries(data?.errors || {}).filter(([k]) => k !== "toxicity").map(([k, v]) => (
        <div key={k} className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">
          {k}: {v}
        </div>
      ))}
      <ToxicityCard tox={s.toxicity} error={data?.errors?.toxicity} />
      {(s.rank_history || []).length > 1 && (
        <Card><CardContent className="p-4">
          <div className="font-semibold text-sm mb-2">SEMrush monthly history</div>
          <table className="w-full text-xs">
            <thead><tr className="text-left text-zinc-500 border-b">
              <th className="py-1">Month</th><th>Rank</th><th>Keywords</th>
              <th>Traffic</th><th>Traffic cost</th><th>Paid kw</th>
            </tr></thead>
            <tbody>
              {(s.rank_history || []).slice(0, 12).map((r) => (
                <tr key={r.date} className="border-b">
                  <td className="py-1.5">{String(r.date || "").length === 8
                    ? `${String(r.date).slice(0, 4)}-${String(r.date).slice(4, 6)}`
                    : r.date}</td>
                  <td>{num(r.rank)}</td>
                  <td>{num(r.organic_keywords)}</td>
                  <td>{num(r.organic_traffic)}</td>
                  <td>{moneyish(r.organic_cost)}</td>
                  <td>{num(r.adwords_keywords)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent></Card>
      )}
      <div className="grid lg:grid-cols-2 gap-4">
        <Card><CardContent className="p-4">
          <div className="font-semibold text-sm mb-2">Top organic keywords</div>
          <table className="w-full text-xs">
            <thead><tr className="text-left text-zinc-500 border-b">
              <th className="py-1">Keyword</th><th>Pos</th><th>Volume</th><th>Traffic %</th><th>CPC</th>
            </tr></thead>
            <tbody>
              {(s.organic_keywords || []).slice(0, 15).map((k, i) => (
                <tr key={`${k.keyword}-${i}`} className="border-b">
                  <td className="py-1.5 pr-2">{k.keyword}</td>
                  <td>{k.position}{k.prev_position && k.prev_position !== k.position ? (
                    <span className={Number(k.prev_position) > Number(k.position) ? "text-emerald-600" : "text-red-500"}>
                      {" "}({k.prev_position}→)
                    </span>) : ""}</td>
                  <td>{num(k.search_volume)}</td>
                  <td>{k.traffic_pct}</td>
                  <td>{k.cpc}</td>
                </tr>
              ))}
              {!(s.organic_keywords || []).length && (
                <tr><td colSpan={5} className="py-3 text-zinc-400">No keyword data.</td></tr>
              )}
            </tbody>
          </table>
        </CardContent></Card>
        <Card><CardContent className="p-4">
          <div className="font-semibold text-sm mb-2">Organic competitors</div>
          <table className="w-full text-xs">
            <thead><tr className="text-left text-zinc-500 border-b">
              <th className="py-1">Domain</th><th>Common kw</th><th>Keywords</th><th>Traffic</th>
            </tr></thead>
            <tbody>
              {(s.competitors || []).map((c, i) => (
                <tr key={`${c.domain}-${i}`} className="border-b">
                  <td className="py-1.5 pr-2 font-medium">{c.domain}</td>
                  <td>{num(c.common_keywords)}</td>
                  <td>{num(c.organic_keywords)}</td>
                  <td>{num(c.organic_traffic)}</td>
                </tr>
              ))}
              {!(s.competitors || []).length && (
                <tr><td colSpan={4} className="py-3 text-zinc-400">No competitor data.</td></tr>
              )}
            </tbody>
          </table>
          <div className="font-semibold text-sm mt-4 mb-2">Top referring domains</div>
          <table className="w-full text-xs">
            <thead><tr className="text-left text-zinc-500 border-b">
              <th className="py-1">Domain</th><th>AS</th><th>Backlinks</th><th>Last seen</th>
            </tr></thead>
            <tbody>
              {(s.referring_domains || []).slice(0, 10).map((d, i) => (
                <tr key={`${d.domain}-${i}`} className="border-b">
                  <td className="py-1.5 pr-2">{d.domain}</td>
                  <td>{d.domain_authority}</td>
                  <td>{num(d.backlinks)}</td>
                  <td>{d.last_seen}</td>
                </tr>
              ))}
              {!(s.referring_domains || []).length && (
                <tr><td colSpan={4} className="py-3 text-zinc-400">No referring-domain data.</td></tr>
              )}
            </tbody>
          </table>
        </CardContent></Card>
      </div>
      <div className="grid lg:grid-cols-2 gap-4">
        <Card><CardContent className="p-4">
          <div className="font-semibold text-sm mb-2">Paid keywords (Google Ads)</div>
          <table className="w-full text-xs">
            <thead><tr className="text-left text-zinc-500 border-b">
              <th className="py-1">Keyword</th><th>Pos</th><th>Volume</th><th>CPC</th><th>Traffic %</th>
            </tr></thead>
            <tbody>
              {(s.paid_keywords || []).slice(0, 12).map((k, i) => (
                <tr key={`${k.keyword}-${i}`} className="border-b">
                  <td className="py-1.5 pr-2">{k.keyword}</td>
                  <td>{k.position}</td>
                  <td>{num(k.search_volume)}</td>
                  <td>{k.cpc}</td>
                  <td>{k.traffic_pct}</td>
                </tr>
              ))}
              {!(s.paid_keywords || []).length && (
                <tr><td colSpan={5} className="py-3 text-zinc-400">No paid keywords found.</td></tr>
              )}
            </tbody>
          </table>
        </CardContent></Card>
        <Card><CardContent className="p-4">
          <div className="font-semibold text-sm mb-2">Top anchors</div>
          <table className="w-full text-xs">
            <thead><tr className="text-left text-zinc-500 border-b">
              <th className="py-1">Anchor text</th><th>Domains</th><th>Backlinks</th>
            </tr></thead>
            <tbody>
              {(s.anchors || []).slice(0, 8).map((a, i) => (
                <tr key={`${a.anchor}-${i}`} className="border-b">
                  <td className="py-1.5 pr-2">{a.anchor || "(empty)"}</td>
                  <td>{num(a.referring_domains)}</td>
                  <td>{num(a.backlinks)}</td>
                </tr>
              ))}
              {!(s.anchors || []).length && (
                <tr><td colSpan={3} className="py-3 text-zinc-400">No anchor data.</td></tr>
              )}
            </tbody>
          </table>
          <div className="font-semibold text-sm mt-4 mb-2">Most linked pages</div>
          <table className="w-full text-xs">
            <thead><tr className="text-left text-zinc-500 border-b">
              <th className="py-1">Page</th><th>Backlinks</th>
            </tr></thead>
            <tbody>
              {(s.top_pages || []).slice(0, 8).map((p, i) => (
                <tr key={`${p.source_url || p.url}-${i}`} className="border-b">
                  <td className="py-1.5 pr-2 break-all">{p.source_url || p.url}</td>
                  <td>{num(p.backlinks)}</td>
                </tr>
              ))}
              {!(s.top_pages || []).length && (
                <tr><td colSpan={2} className="py-3 text-zinc-400">No page data.</td></tr>
              )}
            </tbody>
          </table>
        </CardContent></Card>
      </div>
    </div>
  );
}

/* ── Assignment row ─────────────────────────────────────────────── */
function AssignmentRow({ a, onDelete, hasKey }) {
  const [open, setOpen] = useState(false);
  const [snap, setSnap] = useState(null);
  const [busy, setBusy] = useState("");

  const loadLatest = async () => {
    try {
      const { data } = await api.get(`/semrush/assignments/${a.assignment_id}/latest`);
      setSnap(data.payload);
    } catch { setSnap(null); }
  };

  const toggle = async () => {
    const next = !open;
    setOpen(next);
    if (next && !snap) await loadLatest();
  };

  const refresh = async () => {
    setBusy("refresh");
    try {
      const { data } = await api.post(`/semrush/assignments/${a.assignment_id}/refresh`);
      setSnap(data); setOpen(true);
      const errs = Object.keys(data.errors || {});
      if (errs.length) toast.warning(`Fetched with issues: ${errs.join(", ")}`);
      else toast.success(`SEMrush data refreshed for ${a.target}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Refresh failed"); }
    finally { setBusy(""); }
  };

  return (
    <div className="border rounded-md p-3" data-testid={`semrush-assignment-${a.assignment_id}`}>
      <div className="flex items-center gap-2">
        <button type="button" onClick={toggle} className="flex items-center gap-2 flex-1 text-left">
          {open ? <ChevronDown className="h-4 w-4 text-zinc-400" /> : <ChevronRight className="h-4 w-4 text-zinc-400" />}
          <span className="font-medium">{a.target}</span>
          <Badge variant="outline" className="uppercase text-[10px]">{a.database}</Badge>
          <span className="text-xs text-zinc-400">
            {a.last_refreshed_at ? `refreshed ${a.last_refreshed_at.slice(0, 16).replace("T", " ")}` : "never refreshed"}
          </span>
        </button>
        <Button variant="outline" size="sm" onClick={refresh}
          disabled={busy === "refresh" || !hasKey}
          data-testid={`semrush-refresh-${a.assignment_id}`}>
          {busy === "refresh" ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-1" />}
          Refresh
        </Button>
        <Button variant="ghost" size="sm" onClick={() => onDelete(a)}>
          <Trash2 className="h-4 w-4 text-red-500" />
        </Button>
      </div>
      {open && (snap ? <SnapshotView data={snap} /> : (
        <div className="text-sm text-zinc-400 pt-3">
          No snapshot yet — click Refresh to pull live SEMrush data.
        </div>
      ))}
    </div>
  );
}

/* ── Page ───────────────────────────────────────────────────────── */
export default function SemrushMasterPage() {
  const [master, setMaster] = useState(null);
  const [clients, setClients] = useState([]);
  const [clientId, setClientId] = useState("");
  const [assignments, setAssignments] = useState([]);
  const [newTarget, setNewTarget] = useState("");
  const [newDb, setNewDb] = useState("au");
  const [adding, setAdding] = useState(false);

  const loadMaster = useCallback(async () => {
    try { setMaster((await api.get("/semrush/master")).data); } catch {}
  }, []);
  const loadAssignments = useCallback(async () => {
    try {
      const params = clientId ? { client_id: clientId } : {};
      setAssignments((await api.get("/semrush/assignments", { params })).data);
    } catch { setAssignments([]); }
  }, [clientId]);

  useEffect(() => {
    loadMaster();
    api.get("/clients").then((r) => setClients(r.data)).catch(() => {});
  }, [loadMaster]);
  useEffect(() => { loadAssignments(); }, [loadAssignments]);

  const addAssignment = async () => {
    if (!clientId) { toast.error("Pick a client first"); return; }
    setAdding(true);
    try {
      await api.post("/semrush/assignments",
        { client_id: clientId, target: newTarget, database: newDb });
      setNewTarget(""); loadAssignments();
      toast.success("Domain assigned");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setAdding(false); }
  };

  const del = async (a) => {
    if (!window.confirm(`Remove ${a.target} (${a.database}) from SEMrush tracking?`)) return;
    await api.delete(`/semrush/assignments/${a.assignment_id}`);
    loadAssignments();
  };

  return (
    <div className="space-y-5" data-testid="semrush-page">
      <div className="flex items-center gap-3">
        <TrendingUp className="h-6 w-6 text-[#002FA7]" />
        <div>
          <h1 className="text-2xl font-bold">SEMRUSH · SEO</h1>
          <p className="text-sm text-zinc-500">
            Rankings, organic keywords, competitors and backlinks per client domain —
            powered by your SEMrush API subscription.
          </p>
        </div>
      </div>

      <MasterKeyCard master={master} reload={loadMaster} />

      <Card><CardContent className="p-5 space-y-4">
        <div className="flex items-center justify-between">
          <div className="font-semibold text-sm">Client domain assignments</div>
          <CapabilitiesCheck defaultDomain={assignments[0]?.target} />
        </div>
        <div className="flex flex-wrap gap-2 items-center">
          <Select value={clientId} onValueChange={setClientId}>
            <SelectTrigger className="w-64" data-testid="semrush-client-select">
              <SelectValue placeholder="Select client…" />
            </SelectTrigger>
            <SelectContent>
              {clients.map((c) => (
                <SelectItem key={c.client_id} value={c.client_id}>{c.company_name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Input placeholder="domain.com.au" className="w-56" value={newTarget}
            data-testid="semrush-target-input"
            onChange={(e) => setNewTarget(e.target.value)} />
          <Select value={newDb} onValueChange={setNewDb}>
            <SelectTrigger className="w-24" data-testid="semrush-db-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {(master?.databases || ["au", "us", "uk", "nz"]).map((d) => (
                <SelectItem key={d} value={d}>{d.toUpperCase()}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button onClick={addAssignment} disabled={adding || !newTarget || !clientId}
            data-testid="semrush-add-assignment-btn">
            {adding ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Plus className="h-4 w-4 mr-1" />}
            Assign domain
          </Button>
        </div>
        <div className="space-y-2">
          {assignments.map((a) => (
            <AssignmentRow key={a.assignment_id} a={a} onDelete={del}
              hasKey={!!master?.has_key} />
          ))}
          {!assignments.length && (
            <div className="text-sm text-zinc-400 py-4">
              {clientId ? "No domains assigned to this client yet."
                : "Pick a client to view or assign SEMrush-tracked domains."}
            </div>
          )}
        </div>
      </CardContent></Card>
    </div>
  );
}
